import { useCallback, useEffect } from 'react';
import useAppStore from '../store/useAppStore';

/**
 * Custom hook for managing undo/redo functionality
 * Compatible with Azure deployment and web environments
 */
const useUndoRedo = () => {
  console.log('useUndoRedo hook initialized');
  
  const {
    undoStack,
    redoStack,
    addToUndoStack,
    undo,
    redo,
    canUndo,
    canRedo,
    clearUndoRedoStacks
  } = useAppStore();

  console.log('Hook state:', { undoStackLength: undoStack?.length, redoStackLength: redoStack?.length });

  // Execute undo operation
  const executeUndo = useCallback(() => {
    const canUndoNow = canUndo();
    console.log('executeUndo called, canUndo:', canUndoNow);
    if (canUndoNow) {
      console.log('Executing undo...');
      undo();
    } else {
      console.log('Cannot undo - no actions in stack');
    }
  }, [canUndo, undo]);

  // Execute redo operation
  const executeRedo = useCallback(() => {
    const canRedoNow = canRedo();
    console.log('executeRedo called, canRedo:', canRedoNow);
    if (canRedoNow) {
      console.log('Executing redo...');
      redo();
    } else {
      console.log('Cannot redo - no actions in stack');
    }
  }, [canRedo, redo]);

  // Record action for undo/redo system
  const recordAction = useCallback((action) => {
    addToUndoStack(action);
  }, [addToUndoStack]);

  // Keyboard event handler for CTRL+Z / CTRL+Y
  const handleKeyDown = useCallback((event) => {
    // Debug logging
    console.log('Key pressed:', {
      key: event.key,
      ctrlKey: event.ctrlKey,
      metaKey: event.metaKey,
      shiftKey: event.shiftKey,
      target: event.target.tagName
    });

    // Check if CTRL (or CMD on Mac) is pressed
    const isCtrlOrCmd = event.ctrlKey || event.metaKey;
    
    if (isCtrlOrCmd) {
      if (event.key === 'z' && !event.shiftKey) {
        // CTRL+Z - Undo
        const canUndoNow = canUndo();
        console.log('Undo triggered, canUndo:', canUndoNow);
        event.preventDefault();
        executeUndo();
      } else if (event.key === 'y' || (event.key === 'z' && event.shiftKey)) {
        // CTRL+Y or CTRL+Shift+Z - Redo
        const canRedoNow = canRedo();
        console.log('Redo triggered, canRedo:', canRedoNow);
        event.preventDefault();
        executeRedo();
      }
    }
  }, [executeUndo, executeRedo, canUndo, canRedo]);

  // Set up global keyboard listeners
  useEffect(() => {
    console.log('Setting up global keyboard listeners for undo/redo');
    
    // Add event listener to document for global keyboard shortcuts
    document.addEventListener('keydown', handleKeyDown, true);
    
    // Cleanup function
    return () => {
      console.log('Cleaning up keyboard listeners');
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [handleKeyDown]);

  // Helper function to create schedule cell action
  const createScheduleCellAction = useCallback((
    employeeCode, 
    day, 
    month, 
    year, 
    oldValue, 
    newValue, 
    actionType = 'schedule_update'
  ) => {
    return {
      type: actionType,
      timestamp: new Date().toISOString(),
      data: {
        employeeCode,
        day,
        month,
        year,
        oldValue: oldValue ? { ...oldValue } : null,
        newValue: newValue ? { ...newValue } : null
      },
      description: `${actionType === 'schedule_delete' ? 'Usunięto' : 'Zmieniono'} komórkę ${employeeCode} - ${day}.${month.toString().padStart(2, '0')}.${year}`
    };
  }, []);

  // Helper function to create employee action
  const createEmployeeAction = useCallback((
    employeeCode, 
    oldEmployee, 
    newEmployee, 
    actionType = 'employee_update'
  ) => {
    return {
      type: actionType,
      timestamp: new Date().toISOString(),
      data: {
        employeeCode,
        oldEmployee: oldEmployee ? { ...oldEmployee } : null,
        newEmployee: newEmployee ? { ...newEmployee } : null
      },
      description: `${actionType === 'employee_delete' ? 'Usunięto' : 
                    actionType === 'employee_create' ? 'Dodano' : 'Zmieniono'} pracownika ${employeeCode}`
    };
  }, []);

  // Helper function to create service action
  const createServiceAction = useCallback((
    year, 
    month, 
    day, 
    oldService, 
    newService, 
    actionType = 'service_update'
  ) => {
    return {
      type: actionType,
      timestamp: new Date().toISOString(),
      data: {
        year,
        month,
        day,
        oldService: oldService ? { ...oldService } : null,
        newService: newService ? { ...newService } : null
      },
      description: `${actionType === 'service_delete' ? 'Usunięto' : 
                    actionType === 'service_create' ? 'Dodano' : 'Zmieniono'} służbę ${day}.${month.toString().padStart(2, '0')}.${year}`
    };
  }, []);

  return {
    // Core undo/redo functions
    undo: executeUndo,
    redo: executeRedo,
    canUndo: canUndo(),
    canRedo: canRedo(),
    recordAction,
    clearHistory: clearUndoRedoStacks,
    
    // Helper functions for creating actions
    createScheduleCellAction,
    createEmployeeAction,
    createServiceAction,
    
    // Stack information for debugging/UI
    undoStackLength: undoStack.length,
    redoStackLength: redoStack.length,
    
    // Manual execution (for buttons/menu items)
    executeUndo,
    executeRedo
  };
};

export default useUndoRedo;